import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import { formatYmdSlash } from '../../../lib/date-utils.js';
import type { MemberProjectHistory } from '../../../types/project.js';

/**
 * メンバー担当案件履歴テーブル（完了案件）
 *
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business メンバーが開発系子チケットを担当した完了案件の履歴テーブル。着手日（担当基本設計/開発子チケットの最古更新日）の新しい順に、案件キー・案件名・担当チーム・主担当PL・メンバー実績/予定pt・着手日・完了日を表示
 * @graph-connects none
 */
export function MemberProjectHistoryTable({ projects }: { projects: MemberProjectHistory[] }) {
  const { t } = useTranslation();

  if (projects.length === 0) {
    return (
      <p className="text-center py-8 text-[var(--color-text-muted)]">
        {t('members.detail.noHistory')}
      </p>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full table-fixed">
        <colgroup>
          <col className="w-[120px]" />
          <col className="w-[280px]" />
          <col className="w-[110px]" />
          <col className="w-[110px]" />
          <col className="w-[100px]" />
          <col className="w-[110px]" />
          <col className="w-[110px]" />
        </colgroup>
        <thead>
          <tr className="border-b border-[var(--color-border)]">
            <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-muted)] whitespace-nowrap">
              {t('projects.fields.issueKey')}
            </th>
            <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-muted)] whitespace-nowrap">
              {t('projects.fields.summary')}
            </th>
            <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-muted)] whitespace-nowrap">
              {t('projects.fields.responsibleTeam')}
            </th>
            <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-muted)] whitespace-nowrap">
              {t('projects.fields.pl')}
            </th>
            <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-muted)] whitespace-nowrap">
              {t('members.detail.memberPoints')}
            </th>
            <th
              aria-sort="descending"
              className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-muted)] whitespace-nowrap"
            >
              <span className="inline-flex items-baseline gap-1">
                {t('members.detail.startedDate')}
                <span aria-hidden="true">↓</span>
              </span>
            </th>
            <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-muted)] whitespace-nowrap">
              {t('members.detail.completedDate')}
            </th>
          </tr>
        </thead>
        <tbody>
          {projects.map((project) => (
            <tr
              key={project.id}
              className="border-b border-[var(--color-border)] last:border-b-0 hover:bg-[var(--color-bg-hover)] transition-colors"
            >
              <td className="py-3 px-4 text-sm text-[var(--color-accent-primary)] whitespace-nowrap">
                <Link
                  to="/projects/$pjKey"
                  params={{ pjKey: project.issueKey }}
                  className="hover:underline"
                >
                  {project.issueKey}
                </Link>
              </td>
              <td
                className="py-3 px-4 text-sm text-[var(--color-text-primary)] truncate"
                title={project.summary}
              >
                {project.summary}
              </td>
              <td className="py-3 px-4 text-sm text-[var(--color-text-secondary)] truncate">
                {project.responsibleTeam ?? '-'}
              </td>
              <td className="py-3 px-4 text-sm text-[var(--color-text-secondary)] truncate">
                {project.assignee ?? '-'}
              </td>
              <td className="py-3 px-4 text-sm text-[var(--color-text-secondary)] whitespace-nowrap">
                {project.memberActualPoint}/{project.totalPlannedPoint}
              </td>
              <td className="py-3 px-4 text-sm text-[var(--color-text-secondary)] whitespace-nowrap">
                {project.memberStartedAt ? formatYmdSlash(project.memberStartedAt) : '-'}
              </td>
              <td className="py-3 px-4 text-sm text-[var(--color-text-secondary)] whitespace-nowrap">
                {project.releasedAt ? formatYmdSlash(project.releasedAt) : '-'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
